import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import {
    CATEGORIES,
    acceptAll,
    rejectAll,
    saveConsent,
    getConsent,
    hasDecided,
    onConsentChange,
} from "../../utils/consent";
import "../../styles/CookieBanner.css";

// Three things this gets right that most banners get wrong:
//
//   1. "Reject" is as easy as "Accept" -- same size, same prominence, one
//      click. A banner where refusing takes an extra screen is not a free
//      choice, and regulators have said so repeatedly.
//   2. It does not block the page. No full-screen scrim, nothing trapped
//      behind it. Nothing non-essential loads before a decision anyway
//      (see utils/consent.js), so there is nothing to hold hostage.
//   3. It says what is actually stored. See the audit at the top of
//      utils/consent.js -- this app sets no cookies at all today.
//
// Deliberately NOT a modal: focus is not trapped, because trapping focus for a
// decision that gates nothing yet would make the site harder to use in exchange
// for looking more compliant.

export default function CookieBanner() {
    const [visible, setVisible] = useState(() => !hasDecided());
    const [details, setDetails] = useState(false);
    const [draft, setDraft] = useState(
        () => getConsent() || { necessary: true, analytics: false }
    );

    useEffect(() => {
        // The footer's "Cookie settings" link clears the stored decision; this
        // is how the banner hears about it and comes back.
        return onConsentChange((choices) => {
            if (choices === null) {
                setDraft({ necessary: true, analytics: false });
                setDetails(false);
                setVisible(true);
            } else {
                setVisible(false);
            }
        });
    }, []);

    const close = useCallback(() => setVisible(false), []);

    // Escape dismisses the expanded detail, not the banner itself: closing the
    // banner without an answer would look like a decision and record none.
    useEffect(() => {
        if (!details) return;
        const onKey = (e) => e.key === "Escape" && setDetails(false);
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [details]);

    if (!visible) return null;

    const optional = Object.entries(CATEGORIES).filter(([, c]) => !c.required);

    return (
        <div
            className="cc"
            role="region"
            aria-label="Cookie and storage preferences"
        >
            <div className="cc__inner">
                <div className="cc__text">
                    <h2 className="cc__title">Your data, briefly</h2>
                    <p className="cc__body">
                        WallBurst stores a little information in your browser to keep you
                        signed in and to make checkout work. We don’t use analytics or
                        advertising trackers.{" "}
                        <Link to="/privacy" className="cc__link">
                            Privacy Policy
                        </Link>
                    </p>
                </div>

                <div className="cc__actions">
                    <button
                        type="button"
                        className="wb-btn wb-btn--ghost wb-btn--sm"
                        onClick={() => setDetails((v) => !v)}
                        aria-expanded={details}
                        aria-controls="cc-details"
                    >
                        {details ? "Hide options" : "Options"}
                    </button>
                    {/* Same weight as Accept, on purpose. */}
                    <button
                        type="button"
                        className="wb-btn wb-btn--secondary wb-btn--sm"
                        onClick={() => { rejectAll(); close(); }}
                    >
                        Necessary only
                    </button>
                    <button
                        type="button"
                        className="wb-btn wb-btn--primary wb-btn--sm"
                        onClick={() => { acceptAll(); close(); }}
                    >
                        Accept all
                    </button>
                </div>
            </div>

            {details && (
                <div className="cc__details" id="cc-details">
                    <ul className="cc__cats">
                        {Object.entries(CATEGORIES).map(([key, cat]) => (
                            <li className="cc__cat" key={key}>
                                <label className="cc__cat-head">
                                    <input
                                        type="checkbox"
                                        checked={cat.required || !!draft[key]}
                                        disabled={cat.required}
                                        onChange={(e) =>
                                            setDraft((d) => ({ ...d, [key]: e.target.checked }))
                                        }
                                    />
                                    <span className="cc__cat-label">{cat.label}</span>
                                    {cat.required && (
                                        <span className="cc__cat-tag">Always on</span>
                                    )}
                                </label>
                                <p className="cc__cat-desc">{cat.description}</p>
                            </li>
                        ))}
                    </ul>

                    <div className="cc__details-actions">
                        <button
                            type="button"
                            className="wb-btn wb-btn--primary wb-btn--sm"
                            onClick={() => { saveConsent(draft); close(); }}
                        >
                            Save preferences
                        </button>
                        {optional.length === 0 && (
                            <span className="cc__note">
                                Nothing optional is in use right now.
                            </span>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
